import React, { useEffect, useState, useRef } from 'react';
import { Modal, View, Text, TouchableOpacity, Image, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Tutor } from '../types';
import { useTutor } from '../contexts/TutorContext';
import { useSettings } from '../contexts/SettingsContext';
import { getTranslation } from '../i18n/translations';
import { API_URL } from '@env'; 

const getFlagEmoji = (countryCode: string) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface TutorSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectTutor: (tutor: Tutor) => void;
}

export const TutorSelectionModal: React.FC<TutorSelectionModalProps> = ({
  visible,
  onClose,
  onSelectTutor,
}) => {
  const { selectedTutor, tutors = [], error, isLoading, fetchTutors, setSelectedTutor, getLanguageName } = useTutor();
  const { interfaceLanguage } = useSettings();
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (visible && !hasFetchedRef.current) {
      console.log('Modal visible, fetching tutors');
      fetchTutors();
      hasFetchedRef.current = true;
    }

    if (!visible) {
      hasFetchedRef.current = false;
    }
  }, [visible]);

  const handleSelectTutor = (tutor: Tutor) => {
    console.log('Selecting tutor:', tutor);
    setSelectedTutor(tutor);
    // Update the selected language to match the tutor's language
    onSelectTutor(tutor);
  };

  const renderAvatar = (tutor: Tutor) => {
    if (!tutor.avatar_url) {
      return (
        <View style={[styles.tutorAvatar, styles.avatarError]}>
          <MaterialIcons name="error-outline" size={24} color="#ff6b6b" />
        </View>
      );
    }

    return (
      <Image
        source={{ uri: `${tutor.avatar_url}` }}
        style={styles.tutorAvatar}
        resizeMode="cover"
      />
    );
  };

  const renderTutorItem = (tutor: Tutor) => {
    return (
    <TouchableOpacity
      key={tutor.id}
      style={[
        styles.tutorItem, 
        selectedTutor?.id === tutor.id && styles.selectedTutorItem
      ]}
      onPress={() => handleSelectTutor(tutor)}
    >
      {renderAvatar(tutor)}
      <View style={styles.tutorInfo}>
        <View style={styles.tutorNameRow}>
          {tutor.country_code && (
            <Text style={styles.flagEmoji}>
              {getFlagEmoji(tutor.country_code.toUpperCase())}
            </Text>
          )}
          <Text style={styles.tutorName}>{tutor.name}</Text>
        </View>
        {tutor.personal_background && tutor.teaching_style ? (
          <Text style={styles.tutorBackground}>
            {tutor.personal_background}. {tutor.teaching_style}
          </Text>
        ) : tutor.personal_background ? (
          <Text style={styles.tutorBackground}>{tutor.personal_background}</Text>
        ) : tutor.teaching_style ? (
          <Text style={styles.tutorBackground}>{tutor.teaching_style}</Text>
        ) : null}
        {tutor.preferences && (
          <Text style={styles.tutorPreferences}>{tutor.preferences}</Text>
        )}
      </View>
      {selectedTutor?.id === tutor.id && (
        <View style={styles.checkmarkContainer}>
          <MaterialIcons name="check-circle" size={24} color="#7B3FE4" />
        </View>
      )}
    </TouchableOpacity>
  )};

  const groupTutorsByLanguage = (tutorList: Tutor[]) => {
    if (!Array.isArray(tutorList) || tutorList.length === 0) {
      return {};
    }
    return tutorList.reduce((groups, tutor) => {
      const language = getLanguageName(tutor.language_code);
      if (!groups[language]) {
        groups[language] = [];
      }
      groups[language].push(tutor);
      return groups;
    }, {} as Record<string, Tutor[]>);
  };

  const tutorGroups = groupTutorsByLanguage(tutors);

  if (error) {
    return (
      <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.loadingContent]}>
            <MaterialIcons name="error-outline" size={48} color="#ff6b6b" />
            <Text style={[styles.loadingText, styles.errorText]}>Error: {error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={fetchTutors}>
              <Text style={styles.retryText}>Retry</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.retryButton, styles.cancelButton]} onPress={onClose}>
              <Text style={styles.retryText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  }

  if (isLoading || !Array.isArray(tutors)) {
    return (
      <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.loadingContent]}>
            <ActivityIndicator size="large" color="#4CAF50" />
            <Text style={styles.loadingText}>Loading tutors...</Text>
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleContainer}>
              <Text style={styles.modalTitle}>
                {getTranslation('selectYourTutor', interfaceLanguage)}
              </Text>
              {selectedTutor && (
                <View style={styles.selectedTutorInfo}>
                  <Text style={styles.flagEmoji}>
                    {getFlagEmoji(selectedTutor.country_code.toUpperCase())}
                  </Text>
                  <Text style={styles.selectedTutorText}>
                    Current: {selectedTutor.name}
                  </Text>
                </View>
              )}
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.scrollContent}>
            {Object.entries(tutorGroups).map(([language, tutors]) => (
              <View key={language}>
                <Text style={styles.languageHeader}>{language}</Text>
                {tutors.map(renderTutorItem)}
              </View>
            ))}
          </ScrollView>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    paddingTop: 40, // Add extra padding for status bar on mobile
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90%', // Use percentage instead of vh
    minHeight: 300,
    flex: 1, // Allow content to grow
    alignSelf: 'center',
    overflow: 'hidden',
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative', // Ensure proper stacking
    marginTop: 'auto', // Push to the bottom on mobile
    marginBottom: 'auto', // Center vertically
  },
  scrollContent: {
    padding: 16,
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#fff',
  },
  modalTitleContainer: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  selectedTutorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F5F1FF',
    padding: 8,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  selectedTutorText: {
    fontSize: 14,
    color: '#7B3FE4',
    fontWeight: '500',
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
  },
  tutorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#fff',
    marginBottom: 12,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#eee',
  },
  selectedTutorItem: {
    backgroundColor: '#F5F1FF',
    borderColor: '#7B3FE4',
    shadowColor: "#7B3FE4",
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  tutorAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    marginRight: 16,
    borderWidth: 2,
    borderColor: '#eee',
  },
  tutorInfo: {
    flex: 1,
    gap: 4,
  },
  tutorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  tutorName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  tutorBackground: {
    fontSize: 14,
    color: '#2d2d2d',
    lineHeight: 20,
  },
  tutorPreferences: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
    fontStyle: 'italic',
  },
  languageHeader: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    paddingVertical: 16,
    paddingHorizontal: 8,
    marginTop: 8,
    backgroundColor: '#F5F1FF',
    borderRadius: 8,
    marginBottom: 12,
  },
  checkmarkContainer: {
    backgroundColor: '#F5F1FF',
    padding: 8,
    borderRadius: 20,
  },
  flagEmoji: {
    fontSize: 16,
    marginRight: 8,
    lineHeight: 20,
  },
  headerFlag: {
    fontSize: 16,
    marginLeft: 8,
    lineHeight: 20,
  },
  loadingContent: {
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
    fontSize: 16,
  },
  errorText: {
    color: '#ff6b6b',
    marginTop: 16,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#4CAF50',
    borderRadius: 8,
  },
  cancelButton: {
    backgroundColor: '#666',
    marginTop: 8,
  },
  retryText: {
    color: 'white',
    fontWeight: '600',
  },
  avatarError: {
    backgroundColor: '#fff5f5',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
